import { useState, type ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import type {
  IncidentWorkItemUnbranded,
  IncidentWorkItemWithContext,
  TranslationKey,
} from "@thalamos/vision-common";
import { getLatestPlaceOfSafetyName, tk } from "@thalamos/vision-common";

import { PdfViewerModal } from "../../modals/PdfViewer/PdfViewer.js";
import { SharePageWithoutEmail } from "../../pages/Share/SharePage.js";
import { PopupDialog } from "../Dialog/PopupDialog.js";
import { TypographyI18N } from "../TypographyI18N/TypographyI18N.js";

interface IncidentBannerProps {
  name: string;
  incidentStatus: IncidentWorkItemUnbranded["status"];
  incidentWorkItem: IncidentWorkItemWithContext | undefined;
  isViewShareFormDisabled?: boolean;
  isViewPdfBtnDisabled: boolean;
}

interface ClickableTypographyButtonProps {
  isDisabled: boolean;
  onClick: () => void;
  icon: ReactNode;
  translationKey: TranslationKey;
}

const ClickableTypographyButton = ({
  isDisabled,
  onClick,
  icon,
  translationKey,
}: ClickableTypographyButtonProps) => {
  const theme = useTheme();
  return (
    <Box
      onClick={!isDisabled ? onClick : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {icon}
      <TypographyI18N
        sx={{
          color: isDisabled
            ? theme.palette.text.disabled
            : theme.palette.text.primary,
          display: "flex",
          fontSize: "0.8125rem",
          alignItems: "center",
          cursor: isDisabled ? "not-allowed" : "pointer",
          fontWeight: 600,
          textDecoration: "underline",
        }}
        translationKey={translationKey}
      />
    </Box>
  );
};

export function ShareCodeDialog({
  shareCode,
  onClose,
  incidentWorkItem,
}: {
  shareCode: string;
  incidentWorkItem: IncidentWorkItemWithContext;
  onClose: () => void;
}) {
  const latestPlaceOfSafetyName = getLatestPlaceOfSafetyName(incidentWorkItem);
  const { t } = useTranslation();
  return (
    <PopupDialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableDialogRootPadding={true}
    >
      <IconButton
        onClick={onClose}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1,
        }}
        aria-label={t("common.close")}
      >
        <CloseIcon />
      </IconButton>
      <SharePageWithoutEmail
        activePlaceOfSafetyName={latestPlaceOfSafetyName}
        shareCode={shareCode}
        aspireFrontendUrl={incidentWorkItem?.incidentWorkItem.aspireFrontendUrl}
        incidentWorkItem={incidentWorkItem}
      />
    </PopupDialog>
  );
}

export default function IncidentBanner({
  name,
  incidentStatus,
  incidentWorkItem,
  isViewShareFormDisabled = false,
  isViewPdfBtnDisabled,
}: IncidentBannerProps) {
  const theme = useTheme();
  const [showShareCode, setShowShareCode] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const incidentWorkItemId = incidentWorkItem?.incidentWorkItem.id;
  const shareCode = incidentWorkItem?.incidentWorkItem.shareCode;

  return (
    <Box
      sx={{
        display: "flex",
        padding: { xs: theme.spacing(1, 2), sm: theme.spacing(3) },
        backgroundColor: "banner.background",
        borderRadius: 3,
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: { xs: "grid", sm: "flex" },
          gridTemplateColumns: { xs: "1fr 1fr" },
          gap: { xs: 0, sm: 5, lg: 10 },
          alignItems: { xs: "center", sm: "flex-end" },
          justifyContent: { xs: "center", sm: "center" },
          width: "100%",
        }}
      >
        {/* Name Section */}
        <Box>
          <TypographyI18N
            sx={{ fontSize: "0.875rem", color: "text.secondary" }}
            translationKey={tk("incidentBanner.personName")}
          />
          <Typography
            sx={{
              color: "text.primary",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            {name}
          </Typography>
        </Box>

        {/* Incident Status Section */}
        <Box>
          <TypographyI18N
            sx={{ fontSize: "0.875rem", color: "text.secondary" }}
            translationKey={tk("incidentBanner.incidentStatus")}
          />
          <Typography
            data-testid="incident-status"
            sx={{
              color: "text.primary",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            {incidentStatus}
          </Typography>
        </Box>

        {/* View Shared Code Link */}
        <Box sx={{ mt: { xs: 1, sm: 0 } }}>
          <ClickableTypographyButton
            isDisabled={isViewShareFormDisabled}
            onClick={() => {
              setShowShareCode(true);
            }}
            icon={<ShareOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />}
            translationKey={tk("incidentBanner.viewShareForm")}
          />
        </Box>
        {showShareCode && shareCode && !isViewShareFormDisabled && (
          <ShareCodeDialog
            shareCode={shareCode}
            incidentWorkItem={incidentWorkItem}
            onClose={() => {
              setShowShareCode(false);
            }}
          />
        )}
        {/* View Pdf */}
        <Box sx={{ mt: { xs: 1, sm: 0 } }}>
          <ClickableTypographyButton
            isDisabled={
              !isViewPdfBtnDisabled && incidentWorkItemId ? false : true
            }
            onClick={() => {
              setShowPdf(true);
            }}
            icon={<InsertDriveFileOutlined fontSize="small" sx={{ mr: 0.5 }} />}
            translationKey={tk("incidentBanner.viewPdf")}
          />
        </Box>
        {showPdf && incidentWorkItemId && (
          <PdfViewerModal
            open={showPdf}
            onClose={() => {
              setShowPdf(false);
            }}
            incidentWorkItemId={incidentWorkItemId}
          />
        )}
      </Box>
    </Box>
  );
}
